import Immutable from "immutable";
import AppConstants from "@/constants/AppConstants";
import { FORM_FIELD_DATA_TYPE_TEXT } from "@/constants/FormFieldConstants.mjs";

const {
  ELEMENT_TYPE_FORM_INPUT,
  ELEMENT_TYPE_FORM_SELECT,
  ELEMENT_TYPE_FORM_RADIO,
  ELEMENT_TYPE_FORM_CHECKBOX,
} = AppConstants;

/** The widget tag each form-field element type renders as, per protorenderer's widget API. */
export const FORM_WIDGET_TAGS = {
  [ELEMENT_TYPE_FORM_INPUT]: "shwt-widget-form-input",
  [ELEMENT_TYPE_FORM_SELECT]: "shwt-widget-form-select",
  [ELEMENT_TYPE_FORM_RADIO]: "shwt-widget-form-radio",
  [ELEMENT_TYPE_FORM_CHECKBOX]: "shwt-widget-form-checkbox",
};

/**
 * The content props every form widget takes, per protorenderer's widget API.
 * @typedef {Object} FormWidgetProps
 * @property {string} label
 * @property {boolean} required
 * @property {string} placeholder
 * @property {string} inputType - The native input `type`, one of `FORM_FIELD_DATA_TYPE_*`.
 * @property {boolean} multiline
 * @property {string[]} options - Option labels, in order.
 */

/**
 * Maps content onto the widget's props. Every prop gets a concrete value so a removed key (e.g.
 * multi-line switched off) resets the widget instead of leaving the old value.
 * @param {import("immutable").Map} [content] - The element's `content`.
 * @returns {FormWidgetProps}
 */
export const getFormWidgetProps = (content = Immutable.Map()) => ({
  label: content.get("label", ""),
  required: Boolean(content.get("required")),
  placeholder: content.get("placeholder", ""),
  // The widget's `inputType` property is its native input `type` (the `type` attribute).
  inputType: content.get("dataType", FORM_FIELD_DATA_TYPE_TEXT),
  multiline: Boolean(content.get("multiLine")),
  options: content.get("options")?.toJS() ?? [],
});

/**
 * Resolves once the widget is defined and has rendered its latest props.
 * @param {HTMLElement} widget - The widget host.
 * @returns {Promise<unknown>}
 */
export const whenWidgetRendered = (widget) =>
  widget.ownerDocument.defaultView.customElements
    .whenDefined(widget.localName)
    .then(() => widget.updateComplete);
